import bugsnag from "@bugsnag/js";
import upperFirst from "lodash/upperFirst";
import { FunctionComponent, useEffect, useState } from "react";
import Footer from "@Components/organisms/Footer";
import Header from "@Components/organisms/Header";
import Legal from "@Components/organisms/Legal";
import WithGlobalLoader, { useGlobalLoader } from "@Components/WithGlobalLoader";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { CheerzApiError } from "@Types/api/error";
import { LegalContent, legalPageType } from "@Types/legal";
import { SettingsContent } from "@Types/settings";
import UserContent from "@Types/user";
import { isAppWebView } from "@Utils/device";
import { convertLegalFromApiToContent } from "@Utils/legal";
import { getCsrfToken } from "@Utils/meta";
import { convertSettingsFromApiToContent } from "@Utils/settings";
import { setDataLayerMandatoryPageTrackers } from "@Utils/track";
import { convertFromApiToUserContent } from "@Utils/user";

interface Props {
  localizationSlug: string;
  type: legalPageType;
}

const LegalPages: FunctionComponent<Props> = ({ localizationSlug, type }) => {
  const [pageData, setPageData] = useState<Array<LegalContent>>();
  const [settings, setSettings] = useState<SettingsContent>();
  const [user, setUser] = useState<UserContent>();
  const { setLoading } = useGlobalLoader();

  useEffect(() => {
    setLoading(true);

    const cheerzApiProvider = new CheerzApiProvider(localizationSlug, getCsrfToken(), true);

    cheerzApiProvider
      .getUser()
      .then((userData) => {
        setUser(convertFromApiToUserContent(userData));
        return cheerzApiProvider.getSettings(localizationSlug);
      })
      .then((settingsData) => {
        setSettings(convertSettingsFromApiToContent(settingsData.response));

        switch (type) {
          case "legal":
            return cheerzApiProvider.getLegal(settingsData.response);
          case "privacy":
            return cheerzApiProvider.getPrivacyPolicy(settingsData.response);
          case "terms":
            return cheerzApiProvider.getTermsAndConditions(settingsData.response);
          default:
            const exhaustiveCheck: never = type;
            throw new Error(`Unsupported type ${(exhaustiveCheck as { type: string }).type}`);
        }
      })
      .then((legalData) => {
        setPageData(convertLegalFromApiToContent(legalData.response));
        setLoading(false);
      })
      .catch((error: CheerzApiError) => {
        bugsnag.notify({ name: error.error_code, message: error.developer_message });
      });
  }, []);

  useEffect(() => {
    if (pageData && settings && user) {
      setDataLayerMandatoryPageTrackers(
        settings?.currency,
        settings?.lang,
        upperFirst(type),
        "Other",
        window.location.pathname,
        user
      );
    }
  }, [pageData, settings, user]);

  return isAppWebView() ? (
    pageData ? (
      <Legal data={pageData} type={type} />
    ) : null
  ) : (
    <>
      <Header />
      {pageData ? <Legal data={pageData} type={type} /> : null}
      <Footer />
    </>
  );
};

const LegalPagesContainer: FunctionComponent<Props> = ({ localizationSlug, type }) => {
  return (
    <WithGlobalLoader initialLoading={true}>
      <LegalPages localizationSlug={localizationSlug} type={type} />
    </WithGlobalLoader>
  );
};

export default LegalPagesContainer;
